import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import * as d3 from 'd3';

@Component({
  selector: 'app-inbuilt-bar-chart-v2',
  templateUrl: './inbuilt-bar-chart-v2.component.html',
  styleUrl: './inbuilt-bar-chart-v2.component.css',
  standalone: false
})
export class InbuiltBarChartV2Component implements OnInit, OnDestroy {
  @Input() config: any;
  @Input() elementData: any = null;
  @ViewChild('columnChartContainer', { static: true }) private columnChartContainer!: ElementRef;
  @Output() headingResolved = new EventEmitter<string>();

  displayName: string = '';
  chartData: ChartRowData[] = [];
  seriesKeys: string[] = [];
  seriesConfig: Record<string, { label: string; color: string }> = {};
  valueFormatByKey: Record<string, string> = {};
  resolvedLabelKey: string = '';
  private apiChartConfig: any = null;
  private readonly seriesPalette: string[] = ['#4a4a4a', '#6b46c1', '#ff8c00', '#0ea5e9', '#16a34a', '#e11d48'];

  private readonly destroy$ = new Subject<void>();
  private selectedFilters: any = {};
  private inputSearchFilter: string = '';
  private lastSelectedFiltersSignature = '';
  loading: boolean = false;
  noDataFound: boolean = false;

  constructor(
    private reportsService: ReportsService,
    private inbuiltUtilityService: InbuiltUtilityService
  ) {}

  ngOnInit(): void {
    this.selectedFilters = this.config?.selectedFilters || {};
    this.lastSelectedFiltersSignature = JSON.stringify(this.selectedFilters || {});
    this.subscribeToFilterEvents();
    this.callColumnChartDataApi();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) {
          return;
        }

        const nextFilters = event?.selectedFilters || {};
        const nextInputSearchFilter = event?.inputSearchFilter || '';
        const nextSignature = JSON.stringify({ filters: nextFilters, search: nextInputSearchFilter });
        if (nextSignature === this.lastSelectedFiltersSignature) {
          return;
        }

        this.selectedFilters = nextFilters;
        this.inputSearchFilter = nextInputSearchFilter;
        this.lastSelectedFiltersSignature = nextSignature;
        this.callColumnChartDataApi();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.config) {
      return false;
    }

    const eventReport = String(event?.reportNameFromQueryParams ?? event?.report ?? '');
    const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage = String(this.config?.currentPage?.key ?? '');
    const myTab = String(this.config?.currentTab?.key ?? '');

    if (eventReport && eventReport !== myReport) {
      return false;
    }
    if (eventPage && eventPage !== myPage) {
      return false;
    }
    if (eventTab && eventTab !== myTab) {
      return false;
    }

    return true;
  }

  private callColumnChartDataApi(): void {
    this.loading = true;
    const payload = {
      report: this.config?.reportNameFromQueryParams,
      page: this.config?.currentPage ? this.config.currentPage.key : '',
      tab: this.config?.currentTab ? this.config.currentTab.key : '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || 'bar_chart',
      download: false,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters),
      inputSearchFilter: this.inputSearchFilter
    };

    this.reportsService.getInbuildTableData(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          this.apiChartConfig = res?.chartConfig ?? null;
          this.chartData = Array.isArray(res?.data) ? res.data : [];
          this.noDataFound = this.chartData.length === 0;
          this.buildDynamicSeriesConfig(res?.columnInfo, this.chartData);
          this.bindDisplayText(res?.dynamicHeaderTitle || '');
          this.renderChart();
        },
        error: (error: any) => {
          console.error('Column chart table API error: ', error);
          this.noDataFound = true;
          this.chartData = [];
          this.renderChart();
        }
      });
  }

  get legendItems(): Array<{ label: string; color: string }> {
    return this.seriesKeys.map((key: string) => this.seriesConfig[key]);
  }

  private get chartConfig(): any {
    return this.apiChartConfig ?? this.elementData?.chartConfig ?? {};
  }

  private get labelKey(): string {
    return String(this.chartConfig?.labelKey || '').trim();
  }

  private get labelPosition(): string {
    return String(this.chartConfig?.labelPosition || 'top').trim().toLowerCase() === 'left' ? 'left' : 'top';
  }

  private get colorKey(): string {
    return String(this.chartConfig?.colorkey || this.chartConfig?.colorKey || '').trim();
  }

  get showLegend(): boolean {
    return this.toBooleanFlag(this.chartConfig?.showLegend, true);
  }

  private get showBarScale(): boolean {
    return this.toBooleanFlag(this.chartConfig?.showBar, true);
  }

  private truncateLabelForWidth(label: string, availableWidth: number): string {
    const normalizedLabel = String(label ?? '');
    if (!normalizedLabel) {
      return '';
    }

    if (availableWidth <= 0) {
      return normalizedLabel;
    }

    const averageCharWidth = 8.5;
    const ellipsis = '...';
    const maxCharacters = Math.max(1, Math.floor(availableWidth / averageCharWidth));

    if (normalizedLabel.length <= maxCharacters) {
      return normalizedLabel;
    }

    if (maxCharacters <= ellipsis.length) {
      return ellipsis.slice(0, maxCharacters);
    }

    return `${normalizedLabel.slice(0, maxCharacters - ellipsis.length).trimEnd()}${ellipsis}`;
  }

  private buildDynamicSeriesConfig(columnInfo: any, rows: ChartRowData[]): void {
    this.resolvedLabelKey = this.resolveLabelKey(columnInfo, rows);

    const configuredKeys: string[] = Array.isArray(this.chartConfig?.valueKeyArr)
      ? this.chartConfig.valueKeyArr
          .map((key: any) => String(key ?? '').trim())
          .filter((key: string) => !!key)
      : [];
    const valueKeysFromColumnInfo: string[] = [];
    this.valueFormatByKey = {};

    if (columnInfo && typeof columnInfo === 'object' && !Array.isArray(columnInfo)) {
      Object.keys(columnInfo).forEach((key: string) => {
        const metricType = String(columnInfo[key] ?? '').trim().toUpperCase();
        if (this.resolvedLabelKey && this.norm(key) === this.norm(this.resolvedLabelKey)) {
          return;
        }

        if (metricType === 'SUM' || metricType === 'COUNT') {
          valueKeysFromColumnInfo.push(key);
          this.valueFormatByKey[key] = metricType;
        }
      });
    }

    const fallbackKeys = this.resolveFallbackSeriesKeys(rows);
    const dynamicKeys = configuredKeys.length > 0
      ? configuredKeys
      : (valueKeysFromColumnInfo.length > 0 ? valueKeysFromColumnInfo : fallbackKeys);

    this.seriesKeys = Array.from(new Set<string>(dynamicKeys));
    this.seriesConfig = {};
    this.seriesKeys.forEach((key: string, index: number) => {
      const resolvedMetricType = this.resolveMetricTypeForKey(columnInfo, key);
      if (resolvedMetricType) {
        this.valueFormatByKey[key] = resolvedMetricType;
      }

      this.seriesConfig[key] = {
        label: key,
        color: this.seriesPalette[index % this.seriesPalette.length]
      };
    });
  }

  private resolveFallbackSeriesKeys(rows: ChartRowData[]): string[] {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    const sample = rows[0] || {};
    const segmentNorm = this.norm(this.resolvedLabelKey);
    return Object.keys(sample).filter((key: string) => {
      if (segmentNorm && this.norm(key) === segmentNorm) {
        return false;
      }
      return !Number.isNaN(this.toNumber(sample[key]));
    });
  }

  private norm(value: any): string {
    return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private resolveMetricTypeForKey(columnInfo: any, key: string): string {
    const exactMetricType = String(columnInfo?.[key] ?? '').trim().toUpperCase();
    if (exactMetricType) {
      return exactMetricType;
    }

    const normalizedKey = this.norm(key);
    const matchedKey = Object.keys(columnInfo || {}).find((columnKey: string) => this.norm(columnKey) === normalizedKey);
    return String(matchedKey ? columnInfo?.[matchedKey] ?? '' : '').trim().toUpperCase();
  }

  private resolveLabelKey(columnInfo: any, rows: ChartRowData[]): string {
    if (this.labelKey) {
      return this.labelKey;
    }

    if (columnInfo && typeof columnInfo === 'object' && !Array.isArray(columnInfo)) {
      const dimensionKey = Object.keys(columnInfo).find((key: string) => {
        const metricType = String(columnInfo[key] ?? '').trim().toUpperCase();
        return metricType !== 'SUM' && metricType !== 'COUNT';
      });
      if (dimensionKey) {
        return dimensionKey;
      }
    }

    const sample = rows[0] || {};
    const textKey = Object.keys(sample).find((key: string) => {
      const value = sample[key];
      return typeof value === 'string' && Number.isNaN(Number(value));
    });
    if (textKey) {
      return textKey;
    }

    return Object.keys(sample)[0] || '';
  }

  private getLabelValue(row: ChartRowData): string {
    const configuredLabelKey = this.resolvedLabelKey;
    if (!configuredLabelKey) {
      return '';
    }

    if (row && row[configuredLabelKey] !== undefined && row[configuredLabelKey] !== null && row[configuredLabelKey] !== '') {
      return String(row[configuredLabelKey]);
    }

    const normalizedKey = this.norm(configuredLabelKey);
    const matchedKey = Object.keys(row || {}).find((rowKey: string) => this.norm(rowKey) === normalizedKey);
    if (matchedKey && row[matchedKey] !== undefined && row[matchedKey] !== null && row[matchedKey] !== '') {
      return String(row[matchedKey]);
    }

    return '';
  }

  bindDisplayText(displayText: any): void {
    if (displayText && Object.keys(displayText).length > 0) {
      let key = this.elementData?.name || '';
      if (key.startsWith('{') && key.endsWith('}')) {
        key = key.slice(1, -1).trim();
      }
      const resolved = displayText[key] || '';
      if (resolved) {
        this.displayName = resolved;
        this.headingResolved.emit(resolved);
      }
    }
  }

  private renderChart(): void {
    const host = this.columnChartContainer?.nativeElement;
    if (!host) {
      return;
    }

    d3.select(host).selectAll('*').remove();

    if (!this.chartData.length) {
      return;
    }

    if (!this.seriesKeys.length) {
      return;
    }

    // Keep tooltip anchored to chart container for consistent placement.
    d3.select(host).style('position', 'relative');

    const tooltip = d3
      .select(host)
      .append('div')
      .attr('class', 'column-tooltip')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('opacity', '0')
      .style('left', 'auto')
      .style('right', '8px')
      .style('top', '50%')
      .style('transform', 'translateY(-50%)')
      .style('background', 'rgba(0, 0, 0, 0.72)')
      .style('color', '#ffffff')
      .style('border-radius', '6px')
      .style('padding', '8px 10px')
      .style('font-size', '10px')
      .style('line-height', '1.3')
      .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.2)')
      .style('max-width', '260px')
      .style('z-index', '9999');

    const hostWidth = host.clientWidth || 760;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;
    const targetChartHeight = Math.max(220, Math.min(620, Math.floor(viewportHeight * 0.56)));
    const rowHeight = Math.max(62, Math.min(102, Math.floor(targetChartHeight / Math.max(1, this.chartData.length))));
    const labelPosition = this.labelPosition;
    const maxLabelLength = this.chartData.reduce(
      (max: number, row: ChartRowData) => Math.max(max, this.getLabelValue(row).length),
      0
    );
    const reservedLabelWidth = labelPosition === 'left'
      ? Math.max(160, Math.min(340, maxLabelLength * 9))
      : 0;
    const leftLabelTextWidth = Math.max(0, reservedLabelWidth - 28);
    const margin = { top: 20, right: 96, bottom: 20, left: 24 };
    const width = Math.max(320, hostWidth - margin.left - margin.right);
    const rowTitleOffset = labelPosition === 'left'
      ? Math.max(20, Math.floor(rowHeight * 0.5))
      : Math.max(14, Math.floor(rowHeight * 0.2));
    const barsTopOffset = labelPosition === 'left' ? 8 : rowTitleOffset + 8;
    const barsBandHeight = labelPosition === 'left'
      ? Math.max(30, rowHeight - 16)
      : Math.max(30, rowHeight - (barsTopOffset + 10));
    const height = Math.max(220, this.chartData.length * rowHeight);

    const svg = d3
      .select(host)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const chart = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    const y0 = d3
      .scaleBand()
      .domain(this.chartData.map((d: ChartRowData) => this.getLabelValue(d)))
      .range([0, height])
      .paddingInner(0.24)
      .paddingOuter(0.12);

    const y1 = d3
      .scaleBand()
      .domain(this.seriesKeys)
      .range([barsTopOffset, barsTopOffset + barsBandHeight])
      .padding(0.18);

    const allValues = this.chartData.flatMap((d: ChartRowData) =>
      this.seriesKeys.map((key: string) => this.getSeriesValue(d, key))
    );
    const [minValue, maxValue] = this.resolveXAxisDomain(allValues);

    const x = d3
      .scaleLinear()
      .domain([minValue, maxValue])
      .nice()
      .range([reservedLabelWidth, Math.max(reservedLabelWidth + 80, width - 28)]);

    if (this.showBarScale) {
      const xAxis = d3.axisBottom(x).ticks(5).tickSizeOuter(0).tickFormat((value: number) => {
        return this.formatValueByMetric(this.seriesKeys[0] || '', Number(value));
      });

      const xAxisGroup = chart
        .append('g')
        .attr('class', 'x-axis-scale')
        .attr('transform', `translate(0, ${height})`)
        .call(xAxis as any);

      xAxisGroup.select('.domain').attr('stroke', '#c6cfda');
      xAxisGroup
        .selectAll('.tick line')
        .attr('stroke', '#d8e0ea');
      xAxisGroup
        .selectAll('.tick text')
        .attr('font-size', '10px')
        .attr('fill', '#334155');
    }

    chart
      .append('line')
      .attr('class', 'zero-line')
      .attr('x1', x(0))
      .attr('x2', x(0))
      .attr('y1', 0)
      .attr('y2', height);

    const segmentGroups = chart
      .selectAll('.segment-group')
      .data(this.chartData)
      .enter()
      .append('g')
      .attr('class', 'segment-group')
      .attr('transform', (d: ChartRowData) => `translate(0,${y0(this.getLabelValue(d))})`);

    segmentGroups
      .append('text')
      .attr('class', 'segment-row-title')
      .attr('x', labelPosition === 'left' ? 4 : 0)
      .attr('y', rowTitleOffset)
      .attr('dominant-baseline', labelPosition === 'left' ? 'middle' : null)
      .text((d: ChartRowData) => {
        const label = this.getLabelValue(d);
        return labelPosition === 'left'
          ? this.truncateLabelForWidth(label, leftLabelTextWidth)
          : label;
      })
      .append('title')
      .text((d: ChartRowData) => this.getLabelValue(d));

    segmentGroups
      .selectAll('rect')
      .data((d: ChartRowData) =>
        this.seriesKeys.map((key: string) => ({
          sourceRow: d,
          segment: this.getLabelValue(d),
          key,
          value: this.getSeriesValue(d, key),
          color: this.resolveConfiguredColor(d) || this.seriesConfig[key]?.color || '#4a4a4a',
          label: this.seriesConfig[key]?.label || key || 'Value'
        }))
      )
      .enter()
      .append('rect')
      .attr('class', 'column-bar')
      .attr('x', x(0))
      .attr('y', (d: ColumnDatum) => y1(d.key) ?? 0)
      .attr('width', (d: ColumnDatum) => Math.max(0, x(Math.max(0, d.value)) - x(0)))
      .attr('height', y1.bandwidth())
      .attr('fill', (d: ColumnDatum) => d.color)
      .on('mousemove', (arg1: any, arg2: any) => {
  const datum = this.extractColumnDatum(arg1, arg2);
  if (!datum) {
    tooltip.style('opacity', '0');
    return;
  }
  tooltip
    .style('opacity', '1')
    .style('left', 'auto')
    .style('right', '8px')
    .style('top', '50%')
    .style('transform', 'translateY(-50%)')
    .html(this.getColumnTooltipHtml(datum));
})
      .on('mouseleave', () => {
        tooltip.style('opacity', '0');
      });

    segmentGroups
      .selectAll('.column-value-label')
      .data((d: ChartRowData) =>
        this.seriesKeys.map((key: string) => ({
          key,
          value: this.getSeriesValue(d, key),
          displayValue: this.formatValueByMetric(key, this.getSeriesValue(d, key))
        }))
      )
      .enter()
      .append('text')
      .attr('class', 'column-value-label')
        .attr('x', (d: ColumnLabelDatum) => x(Math.max(0, d.value)) + 6)
      .attr('y', (d: ColumnLabelDatum) => (y1(d.key) ?? 0) + y1.bandwidth() / 2 + 4)
        .attr('text-anchor', 'start')
      .text((d: ColumnLabelDatum) => d.displayValue);
  }

  private formatCompactCurrency(value: number): string {
    const absoluteValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absoluteValue >= 1_000_000_000) {
      return `${sign}$${(absoluteValue / 1_000_000_000).toFixed(2)}B`;
    }
    if (absoluteValue >= 1_000_000) {
      return `${sign}$${(absoluteValue / 1_000_000).toFixed(2)}M`;
    }
    if (absoluteValue >= 1_000) {
      return `${sign}$${(absoluteValue / 1_000).toFixed(2)}K`;
    }
    return `${sign}$${absoluteValue.toFixed(2)}`;
  }

  private formatCount(value: number): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
      minimumFractionDigits: 0
    }).format(value);
  }

  private formatValueByMetric(key: string, value: number): string {
    const metricType = String(this.valueFormatByKey[key] ?? '').toUpperCase();
    if (metricType === 'SUM') {
      return this.formatCompactCurrency(value);
    }
    return this.formatCount(value);
  }

  private resolveXAxisDomain(values: number[]): [number, number] {
    const rawMaxValue = Math.max(0, d3.max(values) ?? 0);
    if (rawMaxValue <= 0) {
      return [0, 1];
    }

    // Keep a little headroom so the largest bar stays below the axis maximum tick.
    const paddedMaxValue = rawMaxValue * 1.08;
    return [0, paddedMaxValue];
  }

  private toBooleanFlag(value: any, fallback: boolean): boolean {
    if (value === undefined || value === null || value === '') {
      return fallback;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }

    return fallback;
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const normalized = String(value)
      .trim()
      .replace(/^\((.*)\)$/, '-$1')
      .replace(/[$,%\s]/g, '')
      .replace(/,/g, '');

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getSeriesValue(row: ChartRowData, key: string): number {
    const matchedKey = this.resolveRowKey(row, key);
    if (matchedKey && row[matchedKey] !== undefined && row[matchedKey] !== null && row[matchedKey] !== '') {
      return this.toNumber(row[matchedKey]);
    }

    return 0;
  }

  private resolveRowKey(row: ChartRowData | undefined, key: string): string | undefined {
    if (!row || !key) {
      return undefined;
    }

    const keys = Object.keys(row || {});
    if (keys.includes(key)) {
      return key;
    }

    const normalizedKey = this.norm(key);
    return keys.find((rowKey: string) => this.norm(rowKey) === normalizedKey);
  }

  private resolveConfiguredColor(row: ChartRowData): string | undefined {
    if (!this.colorKey) {
      return undefined;
    }

    const parentColorConfig = this.elementData?.parentConfig?.color;
    if (!parentColorConfig || typeof parentColorConfig !== 'object' || Array.isArray(parentColorConfig)) {
      return undefined;
    }

    const colorBucketKey = this.resolveObjectKey(parentColorConfig, this.colorKey);
    if (!colorBucketKey) {
      return undefined;
    }

    const colorBucket = parentColorConfig[colorBucketKey];
    if (!colorBucket || typeof colorBucket !== 'object' || Array.isArray(colorBucket)) {
      return undefined;
    }

    const rowColorSourceKey = this.resolveRowKey(row, this.colorKey);
    const rowColorSourceValue = rowColorSourceKey ? row[rowColorSourceKey] : undefined;
    const normalizedColorSourceValue = String(rowColorSourceValue ?? '').trim();
    if (!normalizedColorSourceValue) {
      return undefined;
    }

    const mappedValueKey = this.resolveObjectKey(colorBucket, normalizedColorSourceValue);
    if (!mappedValueKey) {
      return undefined;
    }

    const mappedColor = String(colorBucket[mappedValueKey] ?? '').trim();
    return mappedColor || undefined;
  }

  private resolveObjectKey(source: Record<string, any>, targetKey: string): string | undefined {
    if (!source || !targetKey) {
      return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(source, targetKey)) {
      return targetKey;
    }

    const normalizedTarget = this.norm(targetKey);
    return Object.keys(source).find((key: string) => this.norm(key) === normalizedTarget);
  }

  private getColumnTooltipHtml(d: ColumnDatum): string {
    const segmentName = String(this.getLabelValue(d.sourceRow || ({} as ChartRowData)) || d.segment || '').trim() || this.resolvedLabelKey || 'Label';
    const keyName = String(d.label ?? d.key ?? '').trim() || 'Value';
    const rawValue = this.getSeriesValue(d.sourceRow || ({} as ChartRowData), d.key);
    const formattedValue = this.formatValueByMetric(d.key, rawValue);

    return `
      <div class="tooltip-header" style="font-weight:700;font-size:11px;margin-bottom:6px;padding-bottom:4px;border-bottom:1px solid rgba(255,255,255,0.2);">${segmentName}</div>
      <div class="tooltip-content" style="display:flex;flex-direction:column;gap:4px;">
        <div class="tooltip-row" style="margin:0;white-space:nowrap;display:flex;justify-content:space-between;gap:12px;font-size:11px;">
          <span class="tooltip-key" style="font-weight:600;flex:0 1 auto;">${keyName}:</span>
          <span class="tooltip-value" style="flex:0 0 auto;text-align:right;">${formattedValue}</span>
        </div>
      </div>
    `;
  }

  private extractColumnDatum(arg1: any, arg2: any): ColumnDatum | null {
    if (this.isColumnDatum(arg1)) {
      return arg1;
    }

    if (this.isColumnDatum(arg2)) {
      return arg2;
    }

    return null;
  }

  private isColumnDatum(value: any): value is ColumnDatum {
    return !!value && typeof value === 'object' && typeof value.key === 'string' && value.sourceRow;
  }
}

interface ChartRowData {
  [key: string]: any;
}

interface ColumnDatum {
  sourceRow: ChartRowData;
  segment: string;
  key: string;
  value: number;
  color: string;
  label: string;
}

interface ColumnLabelDatum {
  key: string;
  value: number;
  displayValue: string;
}
